// components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import DatePicker from 'components/shared/DatePicker/DatePicker';
import WeekHoursField from './WeekHoursField';
import { Icon } from '@sdb/planning-ui';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import {
  FteRolloutBox,
  HintText,
  InfoBox,
  WORK_AGREEMENT_COLORS,
  WarningBox,
  WorkAgreementMeta
} from './RegisterAbsenceDialog.styles';

// utils
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import { WEEKDAYS, isPnilContract } from './workAgreementSetting';
import {
  MAX_HOURS_PER_DAY,
  hoursPerDay as calculateHoursPerDay,
  useFormatHoursPerDay
} from './workingDaysHours';

// types
import type { RegisterAbsenceWorkAgreement } from 'RestAPIs/roster/register-absence';
import type { Weekday, WorkAgreementSetting } from './workAgreementSetting';

type WorkAgreementSectionProps = {
  workAgreements: RegisterAbsenceWorkAgreement[];
  selectedWaIds: number[];
  onChange: (selected: number[]) => void;
  dutyCountByWaId: Record<number, number>;
  settings: Record<number, WorkAgreementSetting>;
  onSettingChange: (waId: number, patch: Partial<WorkAgreementSetting>) => void;
  /** shared for the whole absence, but only meaningful with a rollout: below
   *  100% the backend leaves planned duties untouched */
  percentage: number;
  onPercentageChange: (percentage: number) => void;
  /** date on which the inschaling changes inside the period, per work agreement */
  benefitChangeByWaId: Record<number, Date>;
  isWorkingDaysChanged: (waId: number) => boolean;
  onSaveWorkingDays: (waId: number) => Promise<boolean> | void;
  /** days a registered absence already covers, see `shouldMuteDate` */
  shouldMuteDate?: (date: Date) => boolean;
  /** the absence period: the rollout can only create duties inside it */
  periodStart: Date | null;
  periodEnd: Date | null;
};

// number 0 is a real work agreement number, so filter on "absent", not falsy
const label = (workAgreement: RegisterAbsenceWorkAgreement) =>
  [workAgreement.nr, workAgreement.brName]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join(' · ');

const isZeroHoursContract = (workAgreement: RegisterAbsenceWorkAgreement) =>
  workAgreement.contractCode === '0';

/**
 * The explicit work agreement choice. One work agreement selects itself and
 * collapses to a summary line; with several, each work agreement gets its own
 * navigation tab (so it is immediately visible there is more than one) with
 * an "applies to this work agreement" checkbox inside. Planned duties are
 * converted by the rollout itself; optionally new duties are created from a
 * date on the employee's fixed working days.
 */
const WorkAgreementSection = ({
  workAgreements,
  selectedWaIds,
  onChange,
  dutyCountByWaId,
  settings,
  onSettingChange,
  percentage,
  onPercentageChange,
  benefitChangeByWaId,
  isWorkingDaysChanged,
  onSaveWorkingDays,
  shouldMuteDate,
  periodStart,
  periodEnd
}: WorkAgreementSectionProps) => {
  const { t } = useTranslation('common');
  // free input needs a buffer: committing every keystroke would clamp "5" on
  // the way to "50" and fire a rollout for it
  const [percentageInput, setPercentageInput] = useState(String(percentage));

  useEffect(() => {
    setPercentageInput(String(percentage));
  }, [percentage]);

  const commitPercentage = () => {
    const parsed = Number(percentageInput);
    const clamped = Number.isFinite(parsed)
      ? Math.min(100, Math.max(1, Math.round(parsed)))
      : percentage;
    setPercentageInput(String(clamped));
    onPercentageChange(clamped);
  };

  const dutyCountText = (waId: number) => {
    const count = dutyCountByWaId[waId] ?? 0;
    if (count === 0) {
      return t('reactRegisterAbsence.noDutiesInPeriod');
    }
    if (count === 1) {
      return t('reactRegisterAbsence.oneDutyInPeriod');
    }
    return t('reactRegisterAbsence.dutiesInPeriod', { count });
  };

  /** 18 → "18", 17.5 → "17,5" */
  const formatHours = (hours: number) =>
    hours.toLocaleString('nl-NL', { maximumFractionDigits: 2 });

  const formatHoursPerDay = useFormatHoursPerDay();
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleDay = (waId: number, day: Weekday) => {
    const current = settings[waId]?.inputDays ?? [];
    onSettingChange(waId, {
      inputDays: current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day]
    });
  };

  /** hours actually booked per checked working day */
  const hoursPerDay = (setting: WorkAgreementSetting) =>
    calculateHoursPerDay(
      setting.inputWeekHours,
      setting.inputDays.length,
      percentage
    );

  // the hours per day follow the percentage, so the checkboxes immediately
  // show what is actually booked
  const workingDaysLabel = (setting: WorkAgreementSetting) =>
    setting.inputDays.length > 0 && setting.inputWeekHours > 0
      ? t('reactRegisterAbsence.workingDaysLabelWithHours', {
          time: formatHoursPerDay(hoursPerDay(setting))
        })
      : t('reactRegisterAbsence.workingDaysLabel');

  /** By default only planned duties are converted; creating duties on the
   *  fixed working days is an explicit opt-in behind a question. */
  const fteRollout = (workAgreement: RegisterAbsenceWorkAgreement) => {
    const setting = settings[workAgreement.waId];
    if (!setting) {
      return null;
    }

    // PNIL: the question stays visible (the planner should see it exists for
    // other work agreements) but greyed out, with the reason right below it
    if (isPnilContract(workAgreement)) {
      return (
        <Box>
          <FormControlLabel
            sx={{
              display: 'flex',
              ml: 0,
              '& .MuiTypography-root': { fontSize: 14 }
            }}
            control={
              <Checkbox size="small" sx={{ py: 0.25 }} checked={false} />
            }
            label={t('reactRegisterAbsence.useFteRolloutQuestion')}
            disabled
          />
          <InfoBox sx={{ mt: 0.5 }}>
            {t('reactRegisterAbsence.pnilNoRolloutInfo')}
          </InfoBox>
        </Box>
      );
    }

    return (
      <Box>
        <FormControlLabel
          sx={{
            display: 'flex',
            ml: 0,
            '& .MuiTypography-root': { fontSize: 14 }
          }}
          control={
            <Checkbox
              size="small"
              sx={{ py: 0.25 }}
              checked={setting.useFteRollout}
              onChange={(event) =>
                onSettingChange(workAgreement.waId, {
                  useFteRollout: event.target.checked
                })
              }
            />
          }
          label={t('reactRegisterAbsence.useFteRolloutQuestion')}
        />
        {setting.useFteRollout && (
          <FteRolloutBox>
            {/* only relevant here: created duties follow the inschaling, while
                converted duties keep the hours of the existing duty */}
            {benefitChangeByWaId[workAgreement.waId] && (
              <InfoBox sx={{ mb: 1.5 }}>
                {/* the date itself is told at the period */}
                {t('reactRegisterAbsence.benefitChangeHoursInfo')}
              </InfoBox>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {/* The period itself may overlap a registered absence — that is
                  what keeps the overlap visible. The rollout may not: it is
                  where new sickness duties start being created, and a day
                  another absence already owns is not available for that. So the
                  same grey as the period fields, and refused on top of it. */}
              <DatePicker
                label={t('reactRegisterAbsence.fteRolloutStartDate')}
                date={setting.rolloutStartDate}
                onChange={(date) =>
                  onSettingChange(workAgreement.waId, {
                    rolloutStartDate: date
                  })
                }
                shouldMuteDate={shouldMuteDate}
                shouldDisableDate={
                  shouldMuteDate
                    ? (date) => shouldMuteDate(date as Date)
                    : undefined
                }
                // Duties are only ever created inside the absence period — the
                // dialog drops whatever the rollout proposes past the last day
                // — so a date outside it does nothing at all. Bounded rather
                // than merely muted: unlike a covered day, there is no
                // situation here worth keeping visible.
                minDate={periodStart ?? undefined}
                maxDate={periodEnd ?? undefined}
                size="small"
                variant="outlined"
                clearable
              />
              <WeekHoursField
                // remount per work agreement, so the text buffer never leaks
                // from one tab to the next
                key={workAgreement.waId}
                // read-only instead of disabled: the value is information from
                // the work agreement, not a switched-off field (only a
                // zero-hours contract lets the planner override it)
                readOnly={!isZeroHoursContract(workAgreement)}
                sx={{
                  width: 110,
                  '& .MuiInputBase-input, & .MuiFormHelperText-root': {
                    color: percentage < 100 ? 'warning.main' : 'primary.main'
                  }
                }}
                label={t('reactRegisterAbsence.fteRolloutWeekHours')}
                value={setting.inputWeekHours}
                onCommit={(hours) =>
                  onSettingChange(workAgreement.waId, {
                    inputWeekHours: hours
                  })
                }
                // always a helper line, so this field lines up with the
                // percentage next to it
                helperText={
                  percentage < 100 && setting.inputWeekHours > 0
                    ? t('reactRegisterAbsence.effectiveWeekHours', {
                        percentage,
                        hours: formatHours(
                          (setting.inputWeekHours * percentage) / 100
                        )
                      })
                    : // on a zero-hours contract the planner enters the hours;
                      // everywhere else they are contract information
                      t(
                        isZeroHoursContract(workAgreement)
                          ? 'reactRegisterAbsence.weekHoursManualHint'
                          : 'reactRegisterAbsence.weekHoursHint'
                      )
                }
              />
              {/* the percentage only has effect together with the rollout, so
                  it lives here — right next to the hours it scales */}
              {setting.rolloutStartDate && (
                <TextField
                  size="small"
                  type="number"
                  label={t('reactRegisterAbsence.percentageLabel')}
                  value={percentageInput}
                  onChange={(event) => setPercentageInput(event.target.value)}
                  onBlur={commitPercentage}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      commitPercentage();
                    }
                  }}
                  sx={{
                    width: 150,
                    '& .MuiFormHelperText-root': {
                      color: percentage < 100 ? 'warning.main' : 'primary.main'
                    }
                  }}
                  helperText={
                    percentage < 100
                      ? t('reactRegisterAbsence.partialSickness')
                      : t('reactRegisterAbsence.fullSickness')
                  }
                  slotProps={{ htmlInput: { min: 1, max: 100, step: 1 } }}
                />
              )}
            </Box>
            {/* on a zero-hours contract the planner is the only source of the
                week hours: without them every day would book 0 hours, so the
                day chips only appear once the hours are filled in */}
            {isZeroHoursContract(workAgreement) &&
            setting.inputWeekHours <= 0 ? (
              <HintText sx={{ mt: 1, mb: 0 }}>
                {t('reactRegisterAbsence.weekHoursFirstHint')}
              </HintText>
            ) : (
              <Box sx={{ mt: 1 }}>
                <WorkAgreementMeta sx={{ mb: 0.25 }}>
                  {workingDaysLabel(setting)}
                </WorkAgreementMeta>
                {/* one chip per day: no doubt which choice belongs to which day.
                  the save action sits right behind them, so recording the days
                  is part of the same gesture */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  {WEEKDAYS.map((day) => {
                    const active = setting.inputDays.includes(day);
                    return (
                      <Chip
                        key={day}
                        size="small"
                        sx={{ minWidth: 48 }}
                        label={t(`reactRegisterAbsence.weekday.${day}`)}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        disabled={!setting.rolloutStartDate}
                        onClick={() => toggleDay(workAgreement.waId, day)}
                      />
                    );
                  })}
                  {/* a zero-hours contract has no fixed working days to
                    record: the days belong to this absence only */}
                  {!isZeroHoursContract(workAgreement) &&
                    isWorkingDaysChanged(workAgreement.waId) && (
                      <Button
                        // a quiet text link, not a call to action: recording the
                        // days is optional and secondary to the sickness notice
                        variant="text"
                        size="small"
                        disableRipple
                        sx={{
                          ml: 0.5,
                          px: 0.5,
                          minWidth: 0,
                          fontSize: 12,
                          fontWeight: 400,
                          textTransform: 'none',
                          textDecoration: 'underline',
                          background: 'none',
                          '&:hover': {
                            background: 'none',
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={async () => {
                          const saved = await onSaveWorkingDays(
                            workAgreement.waId
                          );
                          if (saved !== false) {
                            showSuccessSnackbar();
                          }
                        }}
                      >
                        {t('reactRegisterAbsence.saveWorkingDays')}
                      </Button>
                    )}
                </Box>
                {/* say what is expected here: the days are rarely known upfront.
                  On a zero-hours contract there is nothing to keep, so that
                  hint does not promise it either. */}
                <HintText sx={{ mt: 0.5, mb: 0 }}>
                  {t(
                    setting.inputDays.length === 0
                      ? 'reactRegisterAbsence.workingDaysEmptyHint'
                      : isZeroHoursContract(workAgreement)
                      ? 'reactRegisterAbsence.workingDaysThisAbsenceHint'
                      : 'reactRegisterAbsence.workingDaysAdjustHint'
                  )}
                </HintText>
                {/* the consequences panel says this too, but that panel can be
                    scrolled out of view — the input causing it says it as well */}
                {hoursPerDay(setting) > MAX_HOURS_PER_DAY && (
                  <WarningBox sx={{ mt: 1 }}>
                    {t('reactRegisterAbsence.rolloutOverMaxHoursWarning')}{' '}
                    {t('reactRegisterAbsence.rolloutOverMaxHoursTip')}
                  </WarningBox>
                )}
              </Box>
            )}
          </FteRolloutBox>
        )}
      </Box>
    );
  };

  if (workAgreements.length === 0) {
    // placeholder of the same height while loading, so the dialog body does
    // not grow (and the centred dialog does not jump) once the data arrives
    return <Box sx={{ minHeight: 72 }} />;
  }

  if (workAgreements.length === 1) {
    const only = workAgreements[0];
    return (
      <Box>
        <WorkAgreementMeta>
          {/* the contract kind explains why the screen differs per work
              agreement (NUC: hours first, PNIL: greyed out) */}
          {[
            label(only),
            only.functionDescription,
            only.contractKind,
            dutyCountText(only.waId)
          ]
            .filter(Boolean)
            .join(' · ')}
        </WorkAgreementMeta>
        {fteRollout(only)}
      </Box>
    );
  }

  const toggle = (waId: number) =>
    onChange(
      selectedWaIds.includes(waId)
        ? selectedWaIds.filter((id) => id !== waId)
        : [...selectedWaIds, waId]
    );

  const activeIndexInRange = Math.min(activeIndex, workAgreements.length - 1);
  const active = workAgreements[activeIndexInRange];
  const activeSelected = selectedWaIds.includes(active.waId);

  // short tab labels ("1e DVB"), so nothing is ever truncated; the line below
  // the tabs names the work agreement that is open
  return (
    <Box>
      <HintText>
        {t('reactRegisterAbsence.workAgreementChoose', {
          count: workAgreements.length
        })}
      </HintText>
      <Tabs
        value={Math.min(activeIndex, workAgreements.length - 1)}
        onChange={(_, index) => setActiveIndex(index)}
        sx={{
          minHeight: 36,
          borderBottom: 1,
          borderColor: 'divider',
          // a thicker underline makes the open tab unmistakable
          '& .MuiTabs-indicator': { height: 3 }
        }}
      >
        {workAgreements.map((workAgreement) => {
          // the briefcase carries the state: green counts, red does not
          const counts = selectedWaIds.includes(workAgreement.waId);
          const color = counts ? 'success.main' : 'error.main';
          return (
            <Tab
              key={workAgreement.waId}
              sx={{
                minHeight: 40,
                textTransform: 'none',
                minWidth: 0,
                fontSize: 15,
                color,
                '&.Mui-selected': {
                  fontWeight: 700,
                  backgroundColor: 'action.hover',
                  borderRadius: '4px 4px 0 0',
                  color
                }
              }}
              aria-label={t(
                counts
                  ? 'reactRegisterAbsence.workAgreementTabAriaCounts'
                  : 'reactRegisterAbsence.workAgreementTabAriaExcluded',
                { number: workAgreement.nr }
              )}
              iconPosition="start"
              icon={<Icon type="briefcase" fontSize="small" />}
              // the real work agreement number, as the overview and the
              // consequences panel name it: an employee's first one may be 0.
              // As a string: MUI treats a label by truthiness, and number 0
              // would lose the icon spacing a labelled tab gets
              label={String(workAgreement.nr)}
            />
          );
        })}
      </Tabs>
      <Box sx={{ pt: 1.5 }}>
        {/* title of the open tab, in its own colour */}
        <Box
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color:
              WORK_AGREEMENT_COLORS[
                activeIndexInRange % WORK_AGREEMENT_COLORS.length
              ]
          }}
        >
          {t('reactRegisterAbsence.workAgreementOpen', {
            number: active.nr,
            name: active.brName ?? label(active)
          })}
        </Box>
        <WorkAgreementMeta sx={{ mb: 1 }}>
          {[
            active.functionDescription,
            active.contractKind,
            dutyCountText(active.waId)
          ]
            .filter(Boolean)
            .join(' · ')}
        </WorkAgreementMeta>
        {/* both choices on one aligned column, same size and font */}
        <FormControlLabel
          sx={{
            display: 'flex',
            ml: 0,
            '& .MuiTypography-root': { fontSize: 14 }
          }}
          control={
            <Checkbox
              size="small"
              sx={{ py: 0.25 }}
              checked={activeSelected}
              onChange={() => toggle(active.waId)}
            />
          }
          label={t('reactRegisterAbsence.workAgreementAppliesTo')}
        />
        {activeSelected && fteRollout(active)}
      </Box>
    </Box>
  );
};

export default WorkAgreementSection;
